import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerFormattingRailToolbarPress } from "./formattingRailToolbarPress";
import { TextResizeHandles } from "./TextResizeHandles";
import { InlineTextEditor } from "./InlineTextEditor";
import { useEditableTextEditSession } from "./useEditableTextEditSession";
import { useEditableTextPointer } from "./useEditableTextPointer";
import { TextBlockShell } from "../render/blocks/TextBlockShell";
import { textBlockHtml } from "../render/blocks/text";
import {
  effectiveTextBlockWidthPx,
  resolveBlockLayoutContext,
} from "../domain/block-width";
import { selectionEquals } from "../domain/selection/equals";
import { isTextBlockEditing } from "../domain/text-editing";
import {
  selectDocument,
  selectEmailSettings,
  selectSelection,
  selectTextEditingBlock,
  selectTextEditReentryGuard,
} from "../domain/selectors";
import { useEditorActions, useEditorStore } from "../domain/store";
import type { TextBlock } from "../domain/types";
import { isInlineTextEmpty } from "./inlineTextBridge";

type EditableTextProps = {
  rowId: string;
  columnId: string;
  block: TextBlock;
};

export function EditableText({ rowId, columnId, block }: EditableTextProps) {
  const { t } = useTranslation();
  const emailDocument = useEditorStore(selectDocument);
  const selection = useEditorStore(selectSelection);
  const textTheme = useEditorStore(selectEmailSettings);
  const textEditingBlock = useEditorStore(selectTextEditingBlock);
  const textEditReentryGuard = useEditorStore(selectTextEditReentryGuard);
  const {
    updateBlock,
    setSelection,
    setTextEditingBlock,
    setTextEditReentryGuard,
  } = useEditorActions();
  const cellRef = useRef<HTMLTableCellElement>(null);
  const exitingRef = useRef(false);
  const toolbarPressRef = useRef(false);
  useEffect(
    () => registerFormattingRailToolbarPress(block.id, toolbarPressRef),
    [block.id],
  );
  const target = { rowId, columnId, blockId: block.id };
  const selected = selectionEquals(selection, { kind: "block", ...target });
  const isEditing = isTextBlockEditing(textEditingBlock, rowId, columnId, block.id);

  const { enterEdit } = useEditableTextEditSession({
    rowId,
    columnId,
    block,
    isEditing,
    target,
    setSelection,
    setTextEditingBlock,
    updateBlock,
    setTextEditReentryGuard,
    cellRef,
    exitingRef,
    toolbarPressRef,
  });

  const { cellHandlers } = useEditableTextPointer({
    block,
    isEditing,
    selected,
    textEditReentryGuard,
    setSelection,
    setTextEditReentryGuard,
    target,
    enterEdit,
  });

  const { contentMaxWidth } = resolveBlockLayoutContext(
    emailDocument,
    rowId,
    columnId,
    block.padding,
  );
  const shellWidth = effectiveTextBlockWidthPx(block, contentMaxWidth);
  const [previewWidthPx, setPreviewWidthPx] = useState<number | null>(null);

  const resizeHandles =
    selected && !isEditing ? (
      <TextResizeHandles
        block={block}
        contentMaxWidth={contentMaxWidth}
        inContentBox={shellWidth == null}
        onPreviewWidthChange={setPreviewWidthPx}
        onPatch={patch => updateBlock(rowId, columnId, block.id, patch)}
      />
    ) : null;

  const shellProps = {
    block,
    textTheme,
    widthPx: shellWidth,
    previewWidthPx: selected ? previewWidthPx : null,
    autoWidth: shellWidth == null,
    outerCellStyle: { position: "relative" as const },
    contentCellProps: {
      ref: cellRef,
      "data-text-block": block.id,
      "data-text-editing": isEditing ? "true" : undefined,
      ...cellHandlers,
    },
    trailing: resizeHandles,
  };

  if (isEditing) {
    return (
      <TextBlockShell {...shellProps}>
        <InlineTextEditor block={block} />
      </TextBlockShell>
    );
  }

  if (isInlineTextEmpty(block)) {
    return (
      <TextBlockShell
        {...shellProps}
        contentClassName="email-builder-block-text email-builder-canvas-block-placeholder">
        <span aria-hidden="true">{t("Text")}</span>
      </TextBlockShell>
    );
  }

  return (
    <TextBlockShell
      {...shellProps}
      dangerouslySetInnerHTML={textBlockHtml(block, textTheme, "raw")}
    />
  );
}
